/* Simply Amazing Apples — Commerce Engine
 * Direct-shopping replacement for the former Build a Luxury Box logic.
 * Expected section root: [data-saa-commerce]
 */

(function () {
  'use strict';

  function initCommerce(root) {
    if (!root || root.dataset.saaCommerceInitialized === 'true') return;
    root.dataset.saaCommerceInitialized = 'true';

    const cards = Array.from(root.querySelectorAll('[data-saa-card]'));
    const filterButtons = Array.from(root.querySelectorAll('[data-saa-filter]'));
    const searchInput = root.querySelector('[data-saa-search]');
    const clearSearchButton = root.querySelector('[data-saa-search-clear]');
    const resetButton = root.querySelector('[data-saa-reset]');
    const emptyResetButton = root.querySelector('[data-saa-empty-reset]');
    const emptyState = root.querySelector('[data-saa-empty]');
    const resultsCount = root.querySelector('[data-saa-results-count]');
    const viewMoreButton = root.querySelector('[data-saa-view-more]');
    const viewMoreLabel = root.querySelector('[data-saa-view-more-label]');
    const viewMoreIcon = root.querySelector('[data-saa-view-more-icon]');
    const status = root.querySelector('[data-saa-status]');

    const initialVisible = Math.max(1, Number(root.dataset.initialVisible || 8));

    let activeCategory = 'all';
    let isViewExpanded = false;

    function normalize(value) {
      return String(value || '')
        .toLowerCase()
        .trim()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    function getMatchingCards() {
      const query = searchInput ? searchInput.value.trim().toLowerCase() : '';

      return cards.filter(function (card) {
        const category = normalize(card.dataset.category);
        const searchText = String(card.dataset.search || '').toLowerCase();

        const categoryMatches =
          activeCategory === 'all' || category === activeCategory;

        const searchMatches =
          query === '' || searchText.includes(query);

        return categoryMatches && searchMatches;
      });
    }

    function updateVisibleCards() {
      const matchingCards = getMatchingCards();

      cards.forEach(function (card) {
        card.hidden = true;
        card.classList.remove('is-revealed');
      });

      const visibleCards = isViewExpanded
        ? matchingCards
        : matchingCards.slice(0, initialVisible);

      visibleCards.forEach(function (card, index) {
        card.hidden = false;
        card.style.setProperty('--reveal-order', index);

        window.requestAnimationFrame(function () {
          card.classList.add('is-revealed');
        });
      });

      if (resultsCount) {
        const count = matchingCards.length;
        resultsCount.textContent =
          count + ' product' + (count === 1 ? '' : 's') + ' found';
      }

      if (emptyState) {
        emptyState.hidden = matchingCards.length !== 0;
      }

      if (viewMoreButton) {
        viewMoreButton.hidden = matchingCards.length <= initialVisible;
      }

      if (viewMoreLabel) {
        viewMoreLabel.textContent = isViewExpanded
          ? root.dataset.viewLessText || 'View Less'
          : root.dataset.viewMoreText || 'View More';
      }

      if (viewMoreIcon) {
        viewMoreIcon.textContent = isViewExpanded ? '↑' : '↓';
      }

      if (clearSearchButton && searchInput) {
        clearSearchButton.hidden = searchInput.value.trim() === '';
      }

      if (resetButton && searchInput) {
        resetButton.hidden =
          activeCategory === 'all' &&
          searchInput.value.trim() === '';
      }

      filterButtons.forEach(function (button) {
        const isActive =
          normalize(button.dataset.saaFilter) === activeCategory;

        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      });
    }

    function setCategory(category, shouldScroll) {
      const normalizedCategory = normalize(category);

      const categoryExists = filterButtons.some(function (button) {
        return normalize(button.dataset.saaFilter) === normalizedCategory;
      });

      activeCategory = categoryExists ? normalizedCategory : 'all';
      isViewExpanded = false;
      updateVisibleCards();

      if (shouldScroll) {
        root.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }

    function resetCommerce() {
      activeCategory = 'all';
      isViewExpanded = false;

      if (searchInput) {
        searchInput.value = '';
      }

      updateVisibleCards();
    }

    filterButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        setCategory(button.dataset.saaFilter, false);
      });
    });

    if (searchInput) {
      searchInput.addEventListener('input', function () {
        isViewExpanded = searchInput.value.trim() !== '';
        updateVisibleCards();
      });
    }

    if (clearSearchButton && searchInput) {
      clearSearchButton.addEventListener('click', function () {
        searchInput.value = '';
        searchInput.focus();
        isViewExpanded = false;
        updateVisibleCards();
      });
    }

    if (resetButton) {
      resetButton.addEventListener('click', resetCommerce);
    }

    if (emptyResetButton) {
      emptyResetButton.addEventListener('click', resetCommerce);
    }

    if (viewMoreButton) {
      viewMoreButton.addEventListener('click', function () {
        isViewExpanded = !isViewExpanded;
        updateVisibleCards();

        if (!isViewExpanded) {
          root.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    }

    root.addEventListener('click', function (event) {
      const addButton = event.target.closest('[data-saa-add]');

      if (!addButton || !root.contains(addButton) || addButton.disabled) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const variantId = Number(addButton.dataset.variantId);

      if (!variantId) {
        if (status) {
          status.textContent = 'This product needs a valid Shopify variant.';
        }
        return;
      }

      const defaultText =
        addButton.dataset.defaultText ||
        addButton.textContent.trim() ||
        'Add to Cart';

      addButton.disabled = true;
      addButton.classList.remove('is-added', 'has-error');
      addButton.classList.add('is-loading');
      addButton.textContent = root.dataset.addingText || 'Adding...';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1
            }
          ]
        })
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) {
              throw new Error(
                data.description ||
                data.message ||
                'Unable to add this item.'
              );
            }

            return data;
          });
        })
        .then(function (data) {
          addButton.classList.remove('is-loading');
          addButton.classList.add('is-added');
          addButton.textContent = root.dataset.addedText || 'Added';

          if (status) {
            status.textContent =
              root.dataset.successMessage || 'Added to your cart.';
          }

          document.dispatchEvent(
            new CustomEvent('cart:refresh', { bubbles: true })
          );

          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                source: 'saa-commerce-engine',
                data: data
              }
            })
          );

          window.setTimeout(function () {
  const shopifyRoot =
    window.Shopify &&
    window.Shopify.routes &&
    window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';

  window.location.assign(shopifyRoot + 'cart');
}, 500);
        })
        .catch(function (error) {
          console.error('Commerce add-to-cart error:', error);

          addButton.classList.remove('is-loading');
          addButton.classList.add('has-error');
          addButton.disabled = false;
          addButton.textContent = defaultText;

          if (status) {
            status.textContent =
              error.message ||
              root.dataset.errorMessage ||
              'This item could not be added. Please try again.';
          }

          window.setTimeout(function () {
            addButton.classList.remove('has-error');
          }, 1800);
        });
    });

    document.addEventListener('click', function (event) {
  const categoryJump = event.target.closest('[data-saa-category-jump]');
  if (!categoryJump) return;

  event.preventDefault();
  setCategory(categoryJump.dataset.saaCategoryJump, true);
});

function openCategoryFromHash() {
  const hash = window.location.hash;

  if (!hash || !hash.startsWith('#shop-')) return;

  const category = hash.replace('#shop-', '');

  setCategory(category, true);
}

updateVisibleCards();

// Handle direct links like /#shop-turtles
window.setTimeout(openCategoryFromHash, 150);

// Handle menu changes after page load
window.addEventListener('hashchange', openCategoryFromHash);
  }

    /* =========================================================
     APPLE OF THE MONTH — ADD TO CART
  ========================================================= */

  document.addEventListener('click', function (event) {
    const addButton = event.target.closest('[data-saa-apple-month-add]');

    if (!addButton || addButton.disabled) return;

    event.preventDefault();

    const variantId = Number(addButton.dataset.variantId);

    if (!variantId) {
      console.error('Apple of the Month needs a valid variant ID.');
      return;
    }

    const defaultText =
      addButton.dataset.defaultText ||
      addButton.textContent.trim() ||
      'Add to Cart';

    addButton.disabled = true;
    addButton.classList.remove('is-added', 'has-error');
    addButton.classList.add('is-loading');
    addButton.textContent = 'Adding...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1
          }
        ]
      })
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) {
            throw new Error(
              data.description ||
              data.message ||
              'Unable to add this item.'
            );
          }

          return data;
        });
      })
      .then(function (data) {
        addButton.classList.remove('is-loading');
        addButton.classList.add('is-added');
        addButton.textContent = 'Added';

        document.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true
          })
        );

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              source: 'saa-apple-of-the-month',
              data: data
            }
          })
        );

        window.setTimeout(function () {
  const shopifyRoot =
    window.Shopify &&
    window.Shopify.routes &&
    window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';

  window.location.assign(shopifyRoot + 'cart');
}, 500);
      })
      .catch(function (error) {
        console.error('Apple of the Month add-to-cart error:', error);

        addButton.classList.remove('is-loading');
        addButton.classList.add('has-error');
        addButton.disabled = false;
        addButton.textContent = 'Try Again';

        window.setTimeout(function () {
          addButton.classList.remove('has-error');
          addButton.textContent = defaultText;
        }, 2200);
      });
  });

  function initAllCommerce(scope) {
    const context = scope || document;

    context
      .querySelectorAll('[data-saa-commerce]')
      .forEach(initCommerce);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAllCommerce(document);
    });
  } else {
    initAllCommerce(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAllCommerce(event.target);
  });
})();